import React from 'react'
import * as Yup from 'yup'
import { Form, Formik, FormikHelpers } from 'formik'
import { GoogleLogin, CredentialResponse } from '@react-oauth/google'

import { Button } from '../../atoms'
import { TextField, PasswordField } from '../../molecules'
import styles from './_.module.css'

const loginSchema = Yup.object({
  email: Yup.string().email('Enter valid email id').required('Enter Email-Id!'),
  password: Yup.string()
    .min(8, 'Password field must be at least 8 characters in length.')
    .required('Enter Password')
})

interface Props {
  link: React.ElementType
  onGoogleLogin: (token: string) => void
  onSubmit: (values: FormType, action: FormikHelpers<FormType>) => void
  onLoginFailure?: () => void
  showHeading?: boolean
  showNavigation?: boolean
  showSignUpLink?: boolean
  initialFormValues?: { email?: string }
  showEmailPasswordFields: boolean
  isGoogleLoginLoading?: boolean
}

export type FormType = {
  email: string
  password: string
}

export const Login: React.FC<Props> = ({
  link: Link,
  onGoogleLogin,
  onLoginFailure,
  onSubmit,
  initialFormValues,
  showHeading = true,
  showNavigation = true,
  showSignUpLink = true,
  showEmailPasswordFields,
  isGoogleLoginLoading = false
}) => {
  const emailParam = new URLSearchParams(window.location.search).get('email')

  const initialFormValue = {
    email: emailParam ?? initialFormValues?.email ?? '',
    password: ''
  }

  return (
    <main className={styles.container}>
      <Formik
        initialValues={initialFormValue}
        onSubmit={(values, actions) => onSubmit(values, actions)}
        validationSchema={loginSchema}
        isInitialValid={false}
        enableReinitialize={initialFormValues?.email ? true : false}
      >
        {({ isValid, isSubmitting, handleBlur, touched, errors }) => (
          <Form className={styles.form}>
            {showHeading ? (
              <h1 className={styles.heading}>Log Into Pepper Content</h1>
            ) : null}
            <div className={styles.googleLoginContainer}>
              <GoogleLogin
                onSuccess={(credentialResponse: CredentialResponse) => {
                  credentialResponse.credential &&
                    onGoogleLogin(credentialResponse.credential)
                }}
                onError={onLoginFailure}
              />
            </div>
            {showEmailPasswordFields ? (
              <>
                <div className={styles.choice}>OR</div>
                <div className={styles.formFields}>
                  <TextField
                    name="email"
                    showError={true}
                    onBlur={handleBlur}
                    error={touched.email && errors.email ? true : false}
                    fieldProps={{
                      placeholder: 'Email Address',
                      size: 'large',
                      autoComplete: 'off'
                    }}
                  />
                  <PasswordField
                    name="password"
                    showError={false}
                    onBlur={handleBlur}
                    error={touched.password && errors.password ? true : false}
                    fieldProps={{
                      placeholder: 'Password',
                      size: 'large'
                    }}
                  />
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    disabled={!isValid}
                    loading={isSubmitting}
                    block={true}
                  >
                    Log In
                  </Button>
                </div>
              </>
            ) : null}
            {showNavigation && showEmailPasswordFields ? (
              <div className={styles.otherLinks}>
                {showSignUpLink ? (
                  <Link to={`/signup${window.location?.search}`}>
                    Don&apos;t have an account
                  </Link>
                ) : (
                  <div />
                )}
                <Link to="/forgot-password">Forgot Password</Link>
              </div>
            ) : null}
          </Form>
        )}
      </Formik>
    </main>
  )
}
